import { useMemo, useState } from 'react';
import styled from 'styled-components';

import DatePickerShortcuts from 'components/insights/filterBar/DatePickerShortcuts';
import FilterBar from 'components/shared/filters/FilterBar';
import { FilterSection } from 'components/shared/filters/FilterSlideout';
import DatePicker from 'components/shared/form/DatePicker';
import Tabs, { TabType } from 'components/Tabs';
import { END_DATE, START_DATE } from 'constants/filterKeysConstants';
import useIsMobile from 'hooks/useIsMobile';
import {
  ReferralsInsightsFilterState,
  useReferralsInsightsActions,
  useReferralsInsightsStore,
} from 'stores/referralsInsightsStore';

import { filtersForSelectedGroupType, getReferralFilterSections } from '../helpers/filters';
import { useReferralsInsightsContext } from '../ReferralsContainer';

type ReferralsFilterBarProps = {
  tabs: TabType[];
  selectedTab?: TabType;
  onTabClick: (tab: TabType) => void;
};

const ReferralsFilterBar = ({ tabs, selectedTab, onTabClick }: ReferralsFilterBarProps) => {
  const { sendingGroupType } = useReferralsInsightsContext();
  const selectedReceivingGroupType = useReferralsInsightsStore((state) => state.selectedReceivingGroupType);
  const filters = useReferralsInsightsStore((state) => state.filters);
  const { setSelectedFilters } = useReferralsInsightsActions();
  const isMobile = useIsMobile();

  const filterSections = useMemo(
    () => getReferralFilterSections(sendingGroupType, selectedReceivingGroupType),
    [sendingGroupType, selectedReceivingGroupType]
  );

  const activeFilters = useMemo(
    () => (selectedReceivingGroupType ? filtersForSelectedGroupType(filters, selectedReceivingGroupType) : filters),
    [filters, selectedReceivingGroupType]
  );

  const startDateFilterValue = activeFilters[START_DATE] as Date;
  const endDateFilterValue = activeFilters[END_DATE] as Date;
  const [selectedStartDate, setSelectedStartDate] = useState<Date>(startDateFilterValue);
  const [selectedEndDate, setSelectedEndDate] = useState<Date>(endDateFilterValue);
  const maxDate = useMemo(() => new Date(), []);
  const minDate = useMemo(() => new Date('05/01/2020'), []);

  const handleClearFilters = () => {
    Object.entries(activeFilters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        setSelectedFilters({ [key]: [] });
      }
    });
  };

  const DatePickerFooter = ({ dateInput }: { dateInput: any }) => (
    <DatePickerShortcuts
      startDate={selectedStartDate}
      endDate={selectedEndDate}
      datePickerRef={dateInput}
      setDates={([startDate, endDate]: Date[]) => {
        setSelectedStartDate(startDate);
        setSelectedEndDate(endDate);
        setSelectedFilters({ [START_DATE]: startDate, [END_DATE]: endDate });
      }}
    />
  );

  return (
    <FilterBar
      showSearchInput={false}
      applyFilters={(appliedFilters: ReferralsInsightsFilterState) => {
        setSelectedFilters(appliedFilters);
      }}
      filters={activeFilters}
      showResults={false}
      filterSections={filterSections as FilterSection[]}
      onClearFilters={handleClearFilters}
      title='Referral Coordination'
      callToActionShowMinWidth={0}
      callToAction={
        <DatePickerContainer>
          <DatePicker
            data-gtm-id='insightsReferralsStartDateFilter'
            $fullWidth={isMobile}
            onCalendarClose={() => setSelectedFilters({ [START_DATE]: selectedStartDate })}
            onChange={(val) => setSelectedStartDate(val as Date)}
            selected={selectedStartDate}
            maxDate={selectedEndDate}
            minDate={minDate}
            clearable={false}
            renderFooter={DatePickerFooter}
            popperPlacement='bottom-start'
          />
          <Emdash>&mdash;</Emdash>
          <DatePicker
            data-gtm-id='insightsReferralsEndDateFilter'
            $fullWidth={isMobile}
            onCalendarClose={() => setSelectedFilters({ [END_DATE]: selectedEndDate })}
            onChange={(val) => setSelectedEndDate(val as Date)}
            selected={selectedEndDate}
            maxDate={maxDate}
            minDate={selectedStartDate}
            clearable={false}
            renderFooter={DatePickerFooter}
            popperPlacement='bottom-start'
          />
        </DatePickerContainer>
      }>
      {tabs.length > 1 && (
        <TabsWrapper>
          <Tabs tabs={tabs} onTabClick={onTabClick} selectedTab={selectedTab} />
        </TabsWrapper>
      )}
    </FilterBar>
  );
};

export default ReferralsFilterBar;

const DatePickerContainer = styled.div`
  display: flex;
  align-items: center;

  input {
    max-width: 160px;

    @media ${({ theme }) => theme.devices.desktop} {
      max-width: 140px;
    }
  }

  @media (max-width: 800px) {
    width: 100%;

    > div:first-child,
    > div:last-child {
      min-width: 0;
    }

    input {
      max-width: none;
    }
  }
`;

const Emdash = styled.div`
  margin: 0 8px;
`;

const TabsWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  width: 100%;
  margin-bottom: -24px;
`;
